<script lang="ts" setup>
import { segmentation, segmentationV1 } from '@thecompaniesapi/shared/client'

const { t } = useI18n()
const { refreshResponseWithApiVersion, version } = useApiDoc()
const api = useApi()

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: `/companies/${segmentation.freeQueries.domain}/email-patterns`,
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.domain'),
    example: `…/companies/${segmentation.freeQueries.domain}/email-patterns`,
    name: 'domain',
    required: true,
    type: 'string',
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.patterns'),
    name: 'patterns',
    type: 'array',

    attributes: [
      {
        description: t('response.pattern'),
        example: '[F1][L]',
        name: 'pattern',
        parent: 'pattern',
        type: 'string',
      },
      {
        description: t('response.usagePercentage'),
        example: '82.6087',
        name: 'usagePercentage',
        parent: 'pattern',
        type: 'number',
      },
    ],
  },
]

async function onVisibilityChange(visibility: boolean) {
  refreshResponseWithApiVersion({
    api,
    execute,
    response,
    visibility,
  })
}

const { data: response, execute, status } = useAsyncData(
  'doc:companies-fetch-email-patterns',
  () => {
    const companyDomain = version.value === 'v1'
      ? segmentationV1.freeQueries.domain
      : segmentation.freeQueries.domain

    return api.companies.emailPatterns.fetch(companyDomain)
  },
  { immediate: false },
)
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "domain": "The company domain."
    },
    "response": {
      "pattern": "The email pattern used.",
      "patterns": "The email patterns used.",
      "usagePercentage": "The usage percentage of this pattern."
    }
  },
  "es": {
    "attributes": {
      "domain": "El nombre de dominio de la empresa."
    },
    "response": {
      "pattern": "El formato de email utilizado.",
      "patterns": "Los formatos de email utilizados.",
      "usagePercentage": "El porcentaje de uso de este formato."
    }
  },
  "fr": {
    "attributes": {
      "domain": "Le domaine de l'entreprise."
    },
    "response": {
      "pattern": "Le modèle d'email utilisé.",
      "patterns": "Les modèles d'emails utilisés.",
      "usagePercentage": "Le pourcentage d'utilisation de ce modèle."
    }
  }
}
</i18n>
